import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Users, Zap, Plus, Download, X, Code, Kanban, BarChart3, MessageSquare, Calendar, Check, ChevronDown, Copy, CheckCircle2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import AppShell from '../components/AppShell';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1]
    || localStorage.getItem('session_token') || '';
}

const headers = () => ({ Authorization: `Bearer ${getToken()}` });

const LEAD_STATUSES = [
  { id: 'new', label: 'New', color: '#B8985A' },
  { id: 'contacted', label: 'Contacted', color: '#5C5347' },
  { id: 'qualified', label: 'Qualified', color: '#4285F4' },
  { id: 'proposal', label: 'Proposal', color: '#C67A5C' },
  { id: 'won', label: 'Won', color: '#1D6B4F' },
  { id: 'lost', label: 'Lost', color: '#999' },
];

const STATUS_MAP = Object.fromEntries(LEAD_STATUSES.map(s => [s.id, s]));

export default function LeadTracker() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddLead, setShowAddLead] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/leads/dashboard`, { headers: headers() }),
      axios.get(`${API}/leads/sources`),
    ]).then(([dashRes, srcRes]) => {
      setDashboard(dashRes.data);
      setSources(srcRes.data.sources || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    const res = await axios.get(`${API}/leads/dashboard`, { headers: headers() });
    setDashboard(res.data);
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4C9B8', borderTopColor: 'transparent' }} />
        </div>
      </AppShell>
    );
  }

  if (!dashboard?.has_business) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto py-16 px-6 text-center">
          <p style={{ color: '#5C5347' }}>Complete your business profile to start tracking leads.</p>
        </div>
      </AppShell>
    );
  }

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'pipeline', label: 'Pipeline', icon: Kanban },
    { id: 'widget', label: 'Widget', icon: Code },
  ];

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto py-8 px-4 md:px-6" data-testid="lead-tracker-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-widest mb-2" style={{ color: '#B8985A' }}>CRM</p>
            <h1 className="text-2xl font-light" style={{ color: '#3C3226' }} data-testid="lead-tracker-title">Lead Tracker</h1>
            <p className="mt-2 text-sm" style={{ color: '#5C5347' }}>Track leads, manage your pipeline, and capture new leads automatically.</p>
          </div>
          <button onClick={() => setShowAddLead(true)} className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider flex items-center gap-1.5 transition-opacity hover:opacity-90" style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }} data-testid="add-lead-btn">
            <Plus size={12} /> Add Lead
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 mb-8 pb-px" style={{ borderBottom: '1px solid #DDD5C8' }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-4 py-2.5 text-[11px] uppercase tracking-wider transition-colors"
              style={{
                color: activeTab === tab.id ? '#3C3226' : '#8C7E6E',
                fontWeight: activeTab === tab.id ? 600 : 400,
                borderBottom: activeTab === tab.id ? '2px solid #B8985A' : '2px solid transparent',
                marginBottom: '-1px',
              }}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={13} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <DashboardTab dashboard={dashboard} sources={sources} refresh={refresh} />
        )}
        {activeTab === 'pipeline' && (
          <PipelineTab sources={sources} onRefresh={refresh} />
        )}
        {activeTab === 'widget' && (
          <WidgetTab />
        )}

        {/* Add Lead Modal */}
        {showAddLead && <AddLeadModal sources={sources} onClose={() => setShowAddLead(false)} onAdded={() => { setShowAddLead(false); refresh(); }} />}
      </div>
    </AppShell>
  );
}

/* ══════════════════════════════════════════
   DASHBOARD TAB (original view)
   ══════════════════════════════════════════ */
function DashboardTab({ dashboard, sources, refresh }) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.post(`${API}/leads/export`, {}, { headers: headers() });
      const rows = res.data.rows || [];
      if (rows.length === 0) { setExporting(false); return; }
      const csvHeaders = Object.keys(rows[0]).join(',');
      const csvRows = rows.map(r => Object.values(r).map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([`${csvHeaders}\n${csvRows}`], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'getchosenfirst_leads.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  const { total_leads, ai_leads_total, traditional_leads_total, ai_leads_this_month, ai_percentage, ai_revenue_estimate, average_job_value, top_sources, monthly_trend, recent_leads } = dashboard;

  const pieData = [
    { name: 'AI Leads', value: ai_leads_total || 0, color: '#1D6B4F' },
    { name: 'Traditional', value: traditional_leads_total || 0, color: '#B8985A' },
  ].filter(d => d.value > 0);

  return (
    <div>
      {/* Export button */}
      <div className="flex justify-end mb-4">
        <button onClick={handleExport} disabled={exporting} className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider flex items-center gap-1.5 transition-opacity hover:opacity-80" style={{ border: '1px solid #DDD5C8', color: '#5C5347' }} data-testid="export-csv-btn">
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Leads', value: total_leads, icon: Users, color: '#3C3226' },
          { label: 'AI Leads', value: ai_leads_total, icon: Zap, color: '#1D6B4F', sub: `${ai_percentage}% from AI` },
          { label: 'This Month (AI)', value: ai_leads_this_month, icon: TrendingUp, color: '#B8985A' },
          { label: 'AI Revenue Est.', value: `$${(ai_revenue_estimate || 0).toLocaleString()}`, icon: TrendingUp, color: '#1D6B4F', sub: `@ $${average_job_value}/job` },
        ].map((kpi, i) => (
          <motion.div key={kpi.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="p-5" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }} data-testid={`kpi-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <kpi.icon size={14} style={{ color: '#8C7E6E' }} />
              <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>{kpi.label}</p>
            </div>
            <p className="text-2xl font-light" style={{ color: kpi.color }}>{kpi.value}</p>
            {kpi.sub && <p className="text-[10px] mt-1" style={{ color: '#8C7E6E' }}>{kpi.sub}</p>}
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }} data-testid="monthly-trend-chart">
          <p className="text-xs uppercase tracking-wider mb-4" style={{ color: '#8C7E6E' }}>Monthly Trend</p>
          {monthly_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthly_trend}>
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#8C7E6E' }} tickFormatter={m => m.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: '#8C7E6E' }} />
                <Tooltip contentStyle={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8', fontSize: 12 }} />
                <Bar dataKey="ai" name="AI Leads" fill="#1D6B4F" radius={[2, 2, 0, 0]} />
                <Bar dataKey="traditional" name="Traditional" fill="#B8985A" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[200px]">
              <p className="text-sm" style={{ color: '#8C7E6E' }}>Add leads to see trends</p>
            </div>
          )}
        </div>

        <div className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }} data-testid="source-breakdown">
          <p className="text-xs uppercase tracking-wider mb-4" style={{ color: '#8C7E6E' }}>Source Breakdown</p>
          {pieData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={140} height={140}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={2}>
                    {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {pieData.map(d => (
                  <div key={d.name} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-xs" style={{ color: '#5C5347' }}>{d.name}: {d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[140px]">
              <p className="text-sm" style={{ color: '#8C7E6E' }}>No leads yet</p>
            </div>
          )}
          {top_sources?.length > 0 && (
            <div className="mt-4 pt-4 space-y-1.5" style={{ borderTop: '1px solid #E8E2D8' }}>
              {top_sources.slice(0, 5).map(s => (
                <div key={s.source_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="text-[11px]" style={{ color: '#5C5347' }}>{s.name}</span>
                  </div>
                  <span className="text-[11px] font-medium" style={{ color: '#3C3226' }}>{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Leads */}
      <div className="mb-8" data-testid="recent-leads">
        <h2 className="text-sm font-medium uppercase tracking-wider mb-4" style={{ color: '#3C3226' }}>Recent Leads</h2>
        {recent_leads?.length > 0 ? (
          <div className="space-y-2">
            {recent_leads.map((lead, i) => {
              const sourceInfo = sources.find(s => s.id === lead.source) || {};
              const statusInfo = STATUS_MAP[lead.status] || STATUS_MAP['new'];
              return (
                <motion.div key={lead.lead_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="p-4 flex items-center justify-between" style={{ backgroundColor: '#FDFBF7', border: '1px solid #E8E2D8' }} data-testid={`lead-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: '#3C3226' }}>{lead.name || 'Unnamed Lead'}</p>
                    <div className="flex items-center gap-3 mt-1">
                      {lead.email && <span className="text-[11px]" style={{ color: '#8C7E6E' }}>{lead.email}</span>}
                      {lead.phone && <span className="text-[11px]" style={{ color: '#8C7E6E' }}>{lead.phone}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium" style={{ backgroundColor: `${statusInfo.color}15`, color: statusInfo.color }}>
                      {statusInfo.label}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium" style={{ backgroundColor: `${sourceInfo.color || '#999'}15`, color: sourceInfo.color || '#999' }}>
                      {sourceInfo.category === 'ai' && <Zap size={8} />}
                      {sourceInfo.name || lead.source}
                    </span>
                    <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{lead.created_at?.slice(0, 10)}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}>
            <Zap size={24} style={{ color: '#B8985A', margin: '0 auto 12px' }} />
            <p className="text-sm" style={{ color: '#5C5347' }}>No leads recorded yet.</p>
            <p className="text-xs mt-1" style={{ color: '#8C7E6E' }}>Add your first lead to start tracking ROI from AI recommendations.</p>
          </div>
        )}
      </div>

      {/* ROI Proof Banner */}
      {ai_leads_total > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-6 text-center" style={{ backgroundColor: '#2C2520' }} data-testid="roi-proof-banner">
          <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#B8985A' }}>GetChosenFirst ROI</p>
          <p className="text-lg font-light" style={{ color: '#F0EBE3' }}>
            GetChosenFirst generated <span className="font-medium" style={{ color: '#B8985A' }}>{ai_leads_total} lead{ai_leads_total !== 1 ? 's' : ''}</span> from AI recommendations
            {ai_revenue_estimate > 0 && <>, worth an estimated <span className="font-medium" style={{ color: '#B8985A' }}>${ai_revenue_estimate.toLocaleString()}</span></>}.
          </p>
        </motion.div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════
   PIPELINE TAB — Mini-CRM Kanban
   ══════════════════════════════════════════ */
function PipelineTab({ sources, onRefresh }) {
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchPipeline = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/leads/pipeline`, { headers: headers() });
      setPipeline(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}/status`, { status: newStatus }, { headers: headers() });
      fetchPipeline();
      onRefresh();
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4C9B8', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!pipeline) return null;

  return (
    <div data-testid="pipeline-tab">
      {/* Pipeline Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-4" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}>
          <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Total Leads</p>
          <p className="text-xl font-light mt-1" style={{ color: '#3C3226' }}>{pipeline.stats.total}</p>
        </div>
        <div className="p-4" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}>
          <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Won</p>
          <p className="text-xl font-light mt-1" style={{ color: '#1D6B4F' }}>{pipeline.stats.won}</p>
        </div>
        <div className="p-4" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}>
          <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Conversion</p>
          <p className="text-xl font-light mt-1" style={{ color: '#B8985A' }}>{pipeline.stats.conversion_rate}%</p>
        </div>
      </div>

      {/* Upcoming Follow-ups */}
      {pipeline.upcoming_followups?.length > 0 && (
        <div className="mb-6 p-4" style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8' }} data-testid="upcoming-followups">
          <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: '#B8985A' }}>Upcoming Follow-ups</p>
          <div className="space-y-2">
            {pipeline.upcoming_followups.slice(0, 5).map(fu => (
              <div key={fu.followup_id} className="flex items-center justify-between text-[12px]">
                <div className="flex items-center gap-2">
                  <Calendar size={11} style={{ color: '#8C7E6E' }} />
                  <span style={{ color: '#3C3226' }}>{fu.lead_name}</span>
                  {fu.note && <span style={{ color: '#8C7E6E' }}>— {fu.note}</span>}
                </div>
                <span style={{ color: '#C67A5C', fontSize: '11px' }}>{fu.date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kanban Columns */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="pipeline-columns">
        {LEAD_STATUSES.map(status => {
          const leads = pipeline.pipeline[status.id] || [];
          return (
            <div key={status.id} className="min-h-[200px]">
              <div className="flex items-center gap-2 mb-3 px-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: status.color }} />
                <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: status.color }}>{status.label}</p>
                <span className="text-[10px] ml-auto" style={{ color: '#8C7E6E' }}>{leads.length}</span>
              </div>
              <div className="space-y-2">
                {leads.map(lead => {
                  const sourceInfo = sources.find(s => s.id === lead.source) || {};
                  return (
                    <motion.div
                      key={lead.lead_id}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="p-3 cursor-pointer hover:shadow-sm transition-shadow"
                      style={{ backgroundColor: '#FDFBF7', border: '1px solid #E8E2D8' }}
                      onClick={() => setSelectedLead(lead)}
                      data-testid={`pipeline-card-${lead.lead_id}`}
                    >
                      <p className="text-[12px] font-medium truncate" style={{ color: '#3C3226' }}>{lead.name || 'Unnamed'}</p>
                      {lead.email && <p className="text-[10px] truncate mt-0.5" style={{ color: '#8C7E6E' }}>{lead.email}</p>}
                      <div className="flex items-center gap-1 mt-2">
                        {sourceInfo.category === 'ai' && <Zap size={8} style={{ color: '#1D6B4F' }} />}
                        <span className="text-[9px]" style={{ color: sourceInfo.color || '#999' }}>{sourceInfo.name || lead.source}</span>
                      </div>
                      {lead.followups?.some(f => !f.completed) && (
                        <div className="flex items-center gap-1 mt-1">
                          <Calendar size={8} style={{ color: '#C67A5C' }} />
                          <span className="text-[9px]" style={{ color: '#C67A5C' }}>Follow-up pending</span>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          sources={sources}
          onClose={() => setSelectedLead(null)}
          onStatusChange={handleStatusChange}
          onRefresh={() => { fetchPipeline(); onRefresh(); }}
        />
      )}
    </div>
  );
}


/* ══════════════════════════════════════════
   LEAD DETAIL MODAL (Notes, Follow-ups, Status)
   ══════════════════════════════════════════ */
function LeadDetailModal({ lead, sources, onClose, onStatusChange, onRefresh }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [followupDate, setFollowupDate] = useState('');
  const [followupNote, setFollowupNote] = useState('');
  const [saving, setSaving] = useState(false);
  const sourceInfo = sources.find(s => s.id === lead.source) || {};

  useEffect(() => {
    axios.get(`${API}/leads/${lead.lead_id}/notes`, { headers: headers() })
      .then(r => setNotes(r.data.notes || []))
      .catch(() => {});
  }, [lead.lead_id]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/leads/${lead.lead_id}/notes`, { text: newNote }, { headers: headers() });
      setNotes(prev => [...prev, res.data.note]);
      setNewNote('');
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const addFollowup = async () => {
    if (!followupDate) return;
    setSaving(true);
    try {
      await axios.post(`${API}/leads/${lead.lead_id}/followup`, { date: followupDate, note: followupNote }, { headers: headers() });
      setFollowupDate('');
      setFollowupNote('');
      onRefresh();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const completeFollowup = async (fuId) => {
    try {
      await axios.put(`${API}/leads/${lead.lead_id}/followup/${fuId}`, {}, { headers: headers() });
      onRefresh();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8' }} onClick={e => e.stopPropagation()} data-testid="lead-detail-modal">
        {/* Header */}
        <div className="p-5 flex items-start justify-between" style={{ borderBottom: '1px solid #E8E2D8' }}>
          <div>
            <p className="text-base font-medium" style={{ color: '#3C3226' }}>{lead.name || 'Unnamed Lead'}</p>
            <div className="flex items-center gap-2 mt-1">
              {lead.email && <span className="text-[11px]" style={{ color: '#8C7E6E' }}>{lead.email}</span>}
              {lead.phone && <span className="text-[11px]" style={{ color: '#8C7E6E' }}>{lead.phone}</span>}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium" style={{ backgroundColor: `${sourceInfo.color || '#999'}15`, color: sourceInfo.color || '#999' }}>
                {sourceInfo.category === 'ai' && <Zap size={8} />}
                {sourceInfo.name || lead.source}
              </span>
              {lead.widget_captured && <span className="text-[10px] px-2 py-0.5" style={{ backgroundColor: '#B8985A15', color: '#B8985A' }}>Widget</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ color: '#8C7E6E' }}><X size={18} /></button>
        </div>

        {/* Status Selector */}
        <div className="px-5 py-3" style={{ borderBottom: '1px solid #E8E2D8' }}>
          <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#8C7E6E' }}>Status</p>
          <div className="flex flex-wrap gap-1.5">
            {LEAD_STATUSES.map(s => (
              <button
                key={s.id}
                onClick={() => onStatusChange(lead.lead_id, s.id)}
                className="px-2.5 py-1 text-[10px] uppercase tracking-wider transition-all"
                style={{
                  backgroundColor: lead.status === s.id ? s.color : 'transparent',
                  color: lead.status === s.id ? '#fff' : s.color,
                  border: `1px solid ${s.color}`,
                }}
                data-testid={`status-btn-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="px-5 py-3" style={{ borderBottom: '1px solid #E8E2D8' }}>
          <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#8C7E6E' }}>Notes</p>
          {notes.length > 0 && (
            <div className="space-y-2 mb-3 max-h-[150px] overflow-y-auto">
              {notes.map(n => (
                <div key={n.note_id} className="p-2" style={{ backgroundColor: '#F7F4EF' }}>
                  <p className="text-[12px]" style={{ color: '#3C3226' }}>{n.text}</p>
                  <p className="text-[9px] mt-1" style={{ color: '#8C7E6E' }}>{n.created_at?.slice(0, 16).replace('T', ' ')}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input value={newNote} onChange={e => setNewNote(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()} placeholder="Add a note..." className="flex-1 px-3 py-2 text-[12px] outline-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }} data-testid="add-note-input" />
            <button onClick={addNote} disabled={saving || !newNote.trim()} className="px-3 py-2 text-[10px] uppercase tracking-wider disabled:opacity-40" style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }} data-testid="add-note-btn">
              <MessageSquare size={12} />
            </button>
          </div>
        </div>

        {/* Follow-ups */}
        <div className="px-5 py-3">
          <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#8C7E6E' }}>Follow-ups</p>
          {lead.followups?.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {lead.followups.map(fu => (
                <div key={fu.followup_id} className="flex items-center justify-between p-2" style={{ backgroundColor: fu.completed ? '#F7F4EF' : '#FDFBF7', border: '1px solid #E8E2D8' }}>
                  <div className="flex items-center gap-2">
                    <Calendar size={10} style={{ color: fu.completed ? '#8C7E6E' : '#C67A5C' }} />
                    <span className="text-[11px]" style={{ color: fu.completed ? '#8C7E6E' : '#3C3226', textDecoration: fu.completed ? 'line-through' : 'none' }}>{fu.date} {fu.note && `— ${fu.note}`}</span>
                  </div>
                  {!fu.completed && (
                    <button onClick={() => completeFollowup(fu.followup_id)} className="p-1" style={{ color: '#1D6B4F' }} data-testid={`complete-fu-${fu.followup_id}`}>
                      <Check size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input type="date" value={followupDate} onChange={e => setFollowupDate(e.target.value)} className="px-2 py-1.5 text-[11px] outline-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }} data-testid="followup-date-input" />
            <input value={followupNote} onChange={e => setFollowupNote(e.target.value)} placeholder="Note (optional)" className="flex-1 px-2 py-1.5 text-[11px] outline-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }} data-testid="followup-note-input" />
            <button onClick={addFollowup} disabled={saving || !followupDate} className="px-3 py-1.5 text-[10px] uppercase tracking-wider disabled:opacity-40" style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }} data-testid="add-followup-btn">
              <Calendar size={12} />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}


/* ══════════════════════════════════════════
   WIDGET TAB — "How did you find us?" embed
   ══════════════════════════════════════════ */
function WidgetTab() {
  const [widgetData, setWidgetData] = useState(null);
  const [widgetStats, setWidgetStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/leads/widget-code`, { headers: headers() }),
      axios.get(`${API}/leads/widget-stats`, { headers: headers() }),
    ]).then(([codeRes, statsRes]) => {
      setWidgetData(codeRes.data);
      setWidgetStats(statsRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const copyCode = () => {
    if (widgetData?.embed_code) {
      navigator.clipboard.writeText(widgetData.embed_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4C9B8', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div data-testid="widget-tab">
      {/* Intro */}
      <div className="mb-6">
        <h2 className="text-lg font-light mb-2" style={{ color: '#3C3226' }}>Lead Capture Widget</h2>
        <p className="text-sm" style={{ color: '#5C5347' }}>
          Embed this widget on your website to automatically capture how new customers found you.
          Every submission flows directly into your Lead Tracker — zero manual entry.
        </p>
      </div>

      {/* Widget Stats */}
      {widgetStats && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Total Widget Leads</p>
            <p className="text-xl font-light mt-1" style={{ color: '#3C3226' }}>{widgetStats.total_widget_leads}</p>
          </div>
          <div className="p-4" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Today</p>
            <p className="text-xl font-light mt-1" style={{ color: '#1D6B4F' }}>{widgetStats.today_widget_leads}</p>
          </div>
        </div>
      )}

      {/* Embed Code */}
      <div className="p-5" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }} data-testid="embed-code-section">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-wider font-medium" style={{ color: '#3C3226' }}>Embed Code</p>
          <button onClick={copyCode} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider transition-all" style={{ border: '1px solid #DDD5C8', color: copied ? '#1D6B4F' : '#5C5347', backgroundColor: copied ? '#1D6B4F10' : 'transparent' }} data-testid="copy-embed-btn">
            {copied ? <><CheckCircle2 size={10} /> Copied</> : <><Copy size={10} /> Copy</>}
          </button>
        </div>
        <p className="text-[11px] mb-3" style={{ color: '#8C7E6E' }}>
          Paste this code into any page on your website. The widget will appear wherever you place it.
        </p>
        <div className="relative">
          <pre className="p-4 text-[11px] overflow-x-auto leading-relaxed" style={{ backgroundColor: '#2C2520', color: '#F0EBE3', maxHeight: '200px' }}>
            <code>{widgetData?.embed_code || 'Loading...'}</code>
          </pre>
        </div>
      </div>

      {/* How it works */}
      <div className="mt-6 p-5" style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8' }}>
        <p className="text-xs uppercase tracking-wider font-medium mb-3" style={{ color: '#3C3226' }}>How it works</p>
        <div className="space-y-3">
          {[
            { step: '1', text: 'Copy the embed code above and paste it into your website HTML' },
            { step: '2', text: 'When a visitor fills out the form, the lead is captured automatically' },
            { step: '3', text: 'All widget leads appear in your Dashboard and Pipeline tabs with a "Widget" badge' },
          ].map(s => (
            <div key={s.step} className="flex items-start gap-3">
              <div className="flex items-center justify-center w-5 h-5 flex-shrink-0 rounded-full" style={{ backgroundColor: '#B8985A15', color: '#B8985A', fontSize: '10px', fontWeight: 600 }}>{s.step}</div>
              <p className="text-[12px]" style={{ color: '#5C5347' }}>{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════
   ADD LEAD MODAL
   ══════════════════════════════════════════ */
function AddLeadModal({ sources, onClose, onAdded }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', source: '', notes: '', value: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/leads`, { ...form, value: parseFloat(form.value) || 0 }, { headers: headers() });
      onAdded();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md mx-4 p-6" style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8' }} onClick={e => e.stopPropagation()} data-testid="add-lead-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-medium uppercase tracking-wider" style={{ color: '#3C3226' }}>Add New Lead</h3>
          <button onClick={onClose} style={{ color: '#8C7E6E' }}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Name</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full mt-1 px-3 py-2.5 text-sm outline-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }} placeholder="John Smith" data-testid="lead-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Email</label>
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full mt-1 px-3 py-2.5 text-sm outline-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }} placeholder="john@email.com" data-testid="lead-email-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Phone</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full mt-1 px-3 py-2.5 text-sm outline-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }} placeholder="(555) 123-4567" data-testid="lead-phone-input" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Source</label>
            <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} className="w-full mt-1 px-3 py-2.5 text-sm outline-none appearance-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }} data-testid="lead-source-select">
              <option value="">Select source...</option>
              <optgroup label="AI Sources">
                {sources.filter(s => s.category === 'ai').map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </optgroup>
              <optgroup label="Traditional Sources">
                {sources.filter(s => s.category === 'traditional').map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </optgroup>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Estimated Value ($)</label>
            <input type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} className="w-full mt-1 px-3 py-2.5 text-sm outline-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }} placeholder="0" data-testid="lead-value-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full mt-1 px-3 py-2.5 text-sm outline-none resize-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }} placeholder="How did they find you?" data-testid="lead-notes-input" />
          </div>
          <button type="submit" disabled={saving} className="w-full py-3 text-[12px] font-medium uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }} data-testid="save-lead-btn">
            {saving ? 'Saving...' : 'Save Lead'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
